/**
 * PSIFY IMMERSIVE — Orchestrator principal
 * Compilat la public/js/immersive.min.js și încărcat DOAR pe homepage-ul imersiv.
 *
 * Flux:
 *  1. detectează profilul (full | lite | static)
 *  2. static  → fără Lenis/WebGL/pinning; conținut vizibil imediat
 *  3. lite    → GSAP reveals + fallback CSS pentru fundal
 *  4. full    → Lenis + coregrafie completă + WebGL (bundle lazy)
 */
import Lenis from 'lenis'
import { detectProfile } from './capabilities.js'
import { initChapters, gsap, ScrollTrigger } from './chapters.js'
import { initCursor } from './cursor.js'
import { initSoundscape } from './soundscape.js'
import { initMobileMenu, initMegaMenu, initNotifications } from './nav.js'

const page = document.querySelector('.psi-page')

function hidePreloader() {
  const pre = document.getElementById('psi-preloader')
  if (pre) pre.classList.add('is-done')
}

function showFallbackBackground() {
  const fallback = document.createElement('div')
  fallback.className = 'psi-webgl-fallback'
  fallback.setAttribute('aria-hidden', 'true')
  document.body.prepend(fallback)
}

function loadWebGL(onReady) {
  const container = document.getElementById('psi-webgl')
  if (!container) return

  const src = container.dataset.src
  if (!src) return

  const script = document.createElement('script')
  script.src = src
  script.async = true

  window.addEventListener('psify:webgl-ready', () => {
    const dark = document.documentElement.getAttribute('data-bs-theme') === 'dark'
    const mobile = window.matchMedia('(pointer: coarse)').matches
    window.PsifyWebGL.init({ container, dark, quality: mobile ? 'mobile' : 'desktop' })
    if (onReady) onReady()
  }, { once: true })

  script.onerror = () => showFallbackBackground()
  document.body.appendChild(script)
}

function initStatic() {
  // profil static: fără animații — asigurăm vizibilitatea totală
  page.classList.add('is-static')
  showFallbackBackground()
  hidePreloader()
}

function initMotion(profile, sound) {
  // --- Lenis smooth scroll ---
  const lenis = new Lenis({
    lerp: 0.09,
    wheelMultiplier: 0.95,
    smoothWheel: true
  })

  lenis.on('scroll', ScrollTrigger.update)
  gsap.ticker.add((time) => lenis.raf(time * 1000))
  gsap.ticker.lagSmoothing(0)

  // scroll programatic prin Lenis (folosit de paginile interioare)
  // + instanța expusă pt. meniul mobil (stop/start cât e deschis)
  if (window.PsifyImmersive) {
    window.PsifyImmersive.scrollTo = (target, opts) => lenis.scrollTo(target, Object.assign({ offset: -70, duration: 1.6 }, opts))
    window.PsifyImmersive.lenis = lenis
  }

  // ancorele interne scrollează prin Lenis
  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const target = document.querySelector(a.getAttribute('href'))
      if (target) {
        e.preventDefault()
        // secțiunea pinned (Transformarea): aterizăm în punctul unde prima
        // frază e deja stabilizată (~18% din progres) — călătoria animată
        // a scroll-ului redă chiar tranziția hero → text, fără scroll în plus
        const isPinnedChapter = target.classList.contains('psi-transform')
        lenis.scrollTo(target, {
          offset: isPinnedChapter ? Math.round(window.innerHeight * 0.55) : -70,
          duration: isPinnedChapter ? 2.2 : 1.6
        })
      }
    })
  })

  // --- fundal ---
  // paginile interioare nu au container WebGL — primesc fundalul static CSS
  if (profile === 'full' && document.getElementById('psi-webgl')) {
    loadWebGL()
  } else {
    showFallbackBackground()
  }
  if (profile === 'full') initCursor()

  // --- coregrafia capitolelor ---
  initChapters({
    onTransformProgress(p) {
      if (window.PsifyWebGL) window.PsifyWebGL.setProgress(p)
      if (sound) sound.setExperienceProgress(p)
    },
    onTransformActive(active) {
      if (sound) sound.setExperienceActive(active)
    },
    // sincronizarea respirației cu cercul CSS (Cap. 7); drumul inelului prin capitole a ieșit pe 07.10.2026 (home.js)
    onBreathSync(t) {
      if (window.PsifyWebGL) window.PsifyWebGL.syncBreath(t)
    }
  })

  // --- dark mode: sincronizează shader-ul ---
  const observer = new MutationObserver(() => {
    const dark = document.documentElement.getAttribute('data-bs-theme') === 'dark'
    if (window.PsifyWebGL) window.PsifyWebGL.setTheme(dark)
  })
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })
}

function initThemeToggle() {
  const btn = document.getElementById('psi-theme-toggle')
  if (!btn) return

  // stare inițială din localStorage (compatibil cu restul site-ului)
  const saved = localStorage.getItem('data-bs-theme')
  if (saved) document.documentElement.setAttribute('data-bs-theme', saved)

  btn.addEventListener('click', () => {
    const current = document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'light' : 'dark'
    document.documentElement.setAttribute('data-bs-theme', current)
    localStorage.setItem('data-bs-theme', current)
  })
}

/* ---------- boot ---------- */

document.addEventListener('DOMContentLoaded', () => {
  if (!page) return

  const profile = detectProfile()
  page.dataset.psiProfile = profile

  // API public pt. paginile interioare (recalc ScrollTrigger după conținut AJAX)
  window.PsifyImmersive = { refresh: () => ScrollTrigger.refresh() }

  initThemeToggle()
  initMobileMenu()
  initMegaMenu()
  initNotifications()
  const sound = initSoundscape(document.getElementById('psi-sound-toggle'))

  if (profile === 'static') {
    initStatic()
    return
  }

  initMotion(profile, sound)

  // preloader: o respirație scurtă (min 0.6s), apoi intrare cinematică.
  // Era 1.4s: pagina e gata la ~1s și ținea vizitatorul degeaba încă 1.5s (măsurat 23.09.2026)
  const start = performance.now()
  window.addEventListener('load', () => {
    const wait = Math.max(0, 600 - (performance.now() - start))
    setTimeout(() => {
      hidePreloader()
      ScrollTrigger.refresh()
    }, wait)
  })

  // plasă de siguranță: preloader-ul nu blochează niciodată > 5s
  setTimeout(hidePreloader, 5000)
})
